import type { Recipe } from '@fitness/api-client';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Copy, Pencil, Plus, Share2, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/Button';
import { ErrorBanner } from '@/components/ErrorBanner';
import { PageHeader } from '@/components/PageHeader';
import { Spinner } from '@/components/Spinner';
import { Badge } from '@/components/exercise/Chip';
import { FoodThumb } from '@/components/food/FoodRow';
import { QuantitySheet } from '@/components/food/QuantitySheet';
import { formatGrams, formatKcal, formatNumber, todayIso } from '@/components/food/format';
import { describeError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useDeleteRecipe, useForkRecipe, useRecipe } from '@/lib/queries/recipes';

export const Route = createFileRoute('/_app/food/recipes/$id/')({
  component: RecipeDetailPage,
});

function RecipeDetailPage() {
  const { id } = Route.useParams();
  const query = useRecipe(id);

  return (
    <>
      <PageHeader
        title={query.data?.name ?? 'Receta'}
        {...(query.data?.prepMinutes ? { subtitle: `${query.data.prepMinutes} min` } : {})}
        action={
          <Link
            to="/food/recipes"
            aria-label="Volver a las recetas"
            className="flex size-11 items-center justify-center rounded-xl border border-line text-ink-muted transition-colors hover:bg-surface-raised active:bg-surface-raised"
          >
            <ArrowLeft className="size-5" aria-hidden="true" />
          </Link>
        }
      />

      {query.isPending ? (
        <div className="flex items-center gap-3 rounded-2xl border border-line bg-surface-raised p-4 text-ink-muted">
          <Spinner size="sm" />
          <span className="text-sm">Cargando la receta…</span>
        </div>
      ) : query.isError ? (
        <ErrorBanner
          title="No hemos podido cargar la receta"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <RecipeDetail recipe={query.data} />
      )}
    </>
  );
}

function RecipeDetail({ recipe }: { recipe: Recipe }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const isMine = recipe.ownerId === user?.id;
  const date = todayIso();

  return (
    <div className="flex flex-col gap-5">
      <section className="rounded-2xl border border-brand/30 bg-brand/5 p-3">
        <p className="text-xs text-ink-faint">
          Por ración · {formatNumber(recipe.servings)}{' '}
          {recipe.servings === 1 ? 'ración' : 'raciones'} en total
        </p>
        <p className="mt-1 text-2xl font-bold text-brand tabular-nums">
          {formatKcal(recipe.kcalPerServing)}
        </p>
        <p className="mt-1 text-sm text-ink-muted tabular-nums">
          P {formatGrams(recipe.proteinGPerServing)} · C {formatGrams(recipe.carbsGPerServing)} · G{' '}
          {formatGrams(recipe.fatGPerServing)}
        </p>
      </section>

      <Button size="lg" block icon={<Plus className="size-5" />} onClick={() => setAdding(true)}>
        Añadir al diario
      </Button>

      {recipe.description !== null ? (
        <p className="text-sm text-ink-muted">{recipe.description}</p>
      ) : null}

      {recipe.tags.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {recipe.tags.map((tag) => (
            <Badge key={tag}>{tag}</Badge>
          ))}
        </div>
      ) : null}

      <section aria-labelledby="ingredientes">
        <h2 id="ingredientes" className="mb-2 text-base font-semibold">
          Ingredientes ({recipe.ingredientsCount})
        </h2>
        <ul className="flex flex-col gap-2">
          {recipe.ingredients.map((ingredient) => (
            <li
              key={ingredient.id}
              className="flex items-center gap-3 rounded-xl border border-line bg-surface-raised p-2"
            >
              <FoodThumb imageUrl={ingredient.food.imageUrl} className="size-10" />
              <span className="min-w-0 flex-1">
                <span className="line-clamp-2 text-sm font-medium text-ink">
                  {ingredient.food.name}
                </span>
                {ingredient.note !== null ? (
                  <span className="block truncate text-xs text-ink-faint">{ingredient.note}</span>
                ) : null}
              </span>
              <span className="shrink-0 text-sm text-ink-muted tabular-nums">
                {formatNumber(ingredient.quantity)} {ingredient.unit}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-ink-faint tabular-nums">
          Receta entera: {formatKcal(recipe.totals.kcal)} · {formatNumber(recipe.totals.weightG)} g
        </p>
      </section>

      {recipe.steps.length > 0 ? (
        <section aria-labelledby="pasos">
          <h2 id="pasos" className="mb-2 text-base font-semibold">
            Preparación
          </h2>
          <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm text-ink-muted marker:text-brand">
            {recipe.steps.map((step, index) => (
              <li key={`${index}-${step.slice(0, 12)}`}>{step}</li>
            ))}
          </ol>
        </section>
      ) : null}

      <RecipeActions recipe={recipe} isMine={isMine} />

      {adding ? (
        <QuantitySheet
          open
          onClose={() => setAdding(false)}
          target={{
            kind: 'recipe',
            recipe: {
              id: recipe.id,
              name: recipe.name,
              imageUrl: recipe.imageUrl,
              perServing: recipe.perServing,
            },
          }}
          date={date}
          meal="dinner"
          onSaved={() => void navigate({ to: '/food', search: { date } })}
        />
      ) : null}
    </div>
  );
}

/** Editar y borrar si es tuya; copiarla si es de otra persona. */
function RecipeActions({ recipe, isMine }: { recipe: Recipe; isMine: boolean }) {
  const navigate = useNavigate();
  const remove = useDeleteRecipe(recipe.id);
  const fork = useForkRecipe(recipe.id);
  const [confirming, setConfirming] = useState(false);

  if (!isMine) {
    return (
      <section className="flex flex-col gap-2 border-t border-line pt-4">
        <Button
          variant="secondary"
          icon={<Copy className="size-4" />}
          loading={fork.isPending}
          onClick={() =>
            fork.mutate(undefined, {
              onSuccess: (copy) => {
                void navigate({ to: '/food/recipes/$id', params: { id: copy.id } });
              },
            })
          }
        >
          Copiar a mis recetas
        </Button>
        {fork.isError ? (
          <p role="alert" className="text-sm text-danger">
            {describeError(fork.error)}
          </p>
        ) : null}
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-2 border-t border-line pt-4">
      <div className="flex flex-wrap gap-2">
        <Link
          to="/food/recipes/$id/edit"
          params={{ id: recipe.id }}
          className="flex min-h-11 items-center gap-2 rounded-xl border border-line bg-surface-raised px-4 text-sm font-semibold text-ink"
        >
          <Pencil className="size-4" aria-hidden="true" />
          Editar
        </Link>
        <Link
          to="/community/compose"
          search={{ type: 'recipe_share', recipeId: recipe.id }}
          className="flex min-h-11 items-center gap-2 rounded-xl border border-line bg-surface-raised px-4 text-sm font-semibold text-ink"
        >
          <Share2 className="size-4" aria-hidden="true" />
          Compartir en la comunidad
        </Link>
        {confirming ? null : (
          <Button
            variant="ghost"
            icon={<Trash2 className="size-4" />}
            onClick={() => setConfirming(true)}
          >
            Eliminar
          </Button>
        )}
      </div>

      {confirming ? (
        <div className="rounded-xl border border-danger/40 bg-danger-surface p-3">
          <p className="text-sm">
            ¿Seguro que quieres eliminar <strong>{recipe.name}</strong>? Lo que ya apuntaste en el
            diario se conserva.
          </p>
          {remove.isError ? (
            <p role="alert" className="mt-2 text-sm text-danger">
              {describeError(remove.error)}
            </p>
          ) : null}
          <div className="mt-3 flex gap-2">
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() =>
                remove.mutate(undefined, {
                  onSuccess: () => {
                    void navigate({ to: '/food/recipes' });
                  },
                })
              }
            >
              Sí, eliminar
            </Button>
            <Button
              variant="secondary"
              disabled={remove.isPending}
              onClick={() => setConfirming(false)}
            >
              Cancelar
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
